import React from "react";

// ---------------------------------------------------------------------------
// UI kit ของ LucidTopup - ชิ้นส่วนเล็ก ๆ ที่ใช้ซ้ำทุกหน้า
// ---------------------------------------------------------------------------

export const money = (value, digits = 2) =>
  Number(value || 0).toLocaleString("th-TH", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

export const baht = (value, digits = 2) => `฿ ${money(value, digits)}`;

export const Panel = ({ children, className = "", plain = false, ...rest }) => (
  <section className={`lt-panel ${plain ? "lt-panel--plain" : ""} ${className}`} {...rest}>
    {children}
  </section>
);

export const PanelHead = ({ step, title, subtitle, action }) => (
  <div className="lt-panel__head">
    {step ? <span className="lt-step">{step}</span> : null}
    <div>
      <h2 className="lt-panel__title">{title}</h2>
      {subtitle ? <p className="lt-panel__subtitle">{subtitle}</p> : null}
    </div>
    {action ? <div style={{ marginLeft: "auto" }}>{action}</div> : null}
  </div>
);

export const SectionTitle = ({ title, subtitle, action }) => (
  <div className="lt-section-title">
    <span className="lt-section-title__bar" />
    <div>
      <h2>{title}</h2>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
    {action ? <div className="lt-section-title__action">{action}</div> : null}
  </div>
);

export const Tag = ({ tone = "muted", children }) => (
  <span className={`lt-tag lt-tag--${tone}`}>{children}</span>
);

const STATUS_MAP = {
  success: { tone: "success", label: "สำเร็จ" },
  pending: { tone: "warning", label: "รอดำเนินการ" },
  failed: { tone: "danger", label: "ไม่สำเร็จ" },
  open: { tone: "warning", label: "รอตอบกลับ" },
  closed: { tone: "muted", label: "ปิดเรื่องแล้ว" },
};

export const StatusTag = ({ status }) => {
  const info = STATUS_MAP[status] || { tone: "muted", label: status || "-" };
  return <Tag tone={info.tone}>{info.label}</Tag>;
};

export const Field = ({ label, children }) => (
  <div className="lt-field">
    {label ? <span className="lt-field__label">{label}</span> : null}
    {children}
  </div>
);

export const TextField = ({ label, ...rest }) => (
  <Field label={label}>
    <input className="lt-input" {...rest} />
  </Field>
);

// เบอร์โทรศัพท์: ตัวเลขล้วน ยาว 10 หลักเท่านั้น
export const PHONE_LENGTH = 10;
export const isValidPhone = (value) => new RegExp(`^\\d{${PHONE_LENGTH}}$`).test(value || "");

export const PhoneField = ({ label, value, onChange, placeholder = "กรอกเบอร์โทรศัพท์ 10 หลัก", ...rest }) => (
  <Field label={label}>
    <input
      className="lt-input"
      type="tel"
      inputMode="numeric"
      autoComplete="tel"
      maxLength={PHONE_LENGTH}
      placeholder={placeholder}
      value={value || ""}
      onChange={(event) => {
        const digits = event.target.value.replace(/\D/g, "").slice(0, PHONE_LENGTH);
        onChange({ ...event, target: { ...event.target, value: digits } });
      }}
      {...rest}
    />
  </Field>
);

export const SelectField = ({ label, options = [], placeholder, ...rest }) => (
  <Field label={label}>
    <select className="lt-select" {...rest}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map((option) => (
        <option key={option.value ?? option} value={option.value ?? option}>
          {option.label ?? option}
        </option>
      ))}
    </select>
  </Field>
);

export const Empty = ({ text = "ยังไม่มีข้อมูล" }) => <div className="lt-empty">{text}</div>;

export const Skeleton = ({ height = 120, className = "", style }) => (
  <div className={`lt-skeleton ${className}`} style={{ height, ...style }} />
);

export const SkeletonGrid = ({ count = 12, height = 190, className = "lt-tile-grid" }) => (
  <div className={className}>
    {Array.from({ length: count }).map((_, index) => (
      <Skeleton key={index} height={height} />
    ))}
  </div>
);

export const Modal = ({ open, title, onClose, children, footer }) => {
  if (!open) return null;
  return (
    <div className="lt-modal-backdrop" onClick={onClose}>
      <div className="lt-modal" onClick={(event) => event.stopPropagation()}>
        <div className="lt-modal__head">
          <h3>{title}</h3>
          <button type="button" className="lt-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        {children}
        {footer ? <div className="lt-mt-24">{footer}</div> : null}
      </div>
    </div>
  );
};

export const Pagination = ({ page, perPage, total, onPageChange, onPerPageChange }) => {
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);
  const maxPage = Math.max(1, Math.ceil(total / perPage));
  return (
    <div className="lt-pagination">
      <span>
        แถวต่อหน้า:
        <select
          value={perPage}
          onChange={(event) => onPerPageChange(Number(event.target.value))}
          style={{ marginLeft: 8 }}
        >
          {[10, 25, 50].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </span>
      <span>
        {start} - {end} จาก {total}
      </span>
      <span className="lt-flex lt-gap-8">
        <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          ‹
        </button>
        <button type="button" disabled={page >= maxPage} onClick={() => onPageChange(page + 1)}>
          ›
        </button>
      </span>
    </div>
  );
};

export const Switch = ({ checked, onChange, label }) => (
  <div className="lt-switch-row">
    <span>{label}</span>
    <button
      type="button"
      className={`lt-switch ${checked ? "is-on" : ""}`}
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      aria-label={label}
    />
  </div>
);

export const PayLogo = ({ text, sub }) => (
  <div className="lt-paylogo">
    <span>{text}</span>
    {sub ? <small>{sub}</small> : null}
  </div>
);

export const Qty = ({ value, onChange, min = 1, max = 99 }) => (
  <div className="lt-qty">
    <button type="button" onClick={() => onChange(Math.max(min, value - 1))}>
      −
    </button>
    <span>{value}</span>
    <button type="button" onClick={() => onChange(Math.min(max, value + 1))}>
      +
    </button>
  </div>
);
